/**
 * StrategyView — the team's strategy page (Figma 13 Strategy, round 5). The
 * page's index is the start (A01) with the brand's strategies; ?strategy=<id>
 * opens one as a deck in slide view or grid view, with Creo in the shell's
 * rail. Creo creates a new strategy from the start's first line (its
 * CreateStrategy tool), and the page opens it when it does. This page owns the
 * other mutations: rename, go live, back to draft, archive, next period, send
 * to a client and stop sharing. The deck (StrategyDocumentView) draws.
 *
 * Strategy v3 (docs/strategy-v3-plan.md): the deck is the approved 13 Strategy
 * plate. Approvals, comments and versions are phase 5, so their buttons are
 * not here.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, MoreHorizontal, Plus, Send } from "lucide-react";
import { Dialog, DialogTrigger } from "react-aria-components";
import { useNavigate, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { queue } from "@/components/Toast";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { usePermissions } from "@/hooks/usePermissions";
import { useGetBrands } from "@/hooks/useGetBrands";
import type { StrategyListItem } from "@/types/strategy.type";
import { useAgentFocus, agentRail, useAgentRailStatusStore } from "@/app/routes/dashboard/agent/agentFocusStore";
import { useAgentRunStore } from "@/app/routes/dashboard/agent/agentRunStore";
import {
  useDeleteStrategy,
  useRenameStrategy,
  useStrategies,
  useStrategyDetail,
  useStrategySlideImage,
} from "@/app/routes/dashboard/strategy/api/useStrategies";
import { lifecycleErrorMessage, useStrategyLifecycle } from "@/app/routes/dashboard/strategy/api/useStrategyLifecycle";
import { useRevokeStrategyShare, useStrategyShares } from "@/app/routes/dashboard/strategy/api/useStrategyShares";
import { useCalendarSettings } from "@/app/routes/dashboard/content-calendar/api/useContentCalendar";
import StrategyDocumentView, { type DeckView } from "@/app/routes/dashboard/strategy/components/StrategyDocumentView";
import { useStrategySlideContext } from "../strategy/strategySlideContext";
import type { SlideTextSource } from "../strategy/components/slideTextSource";
import type { StrategySlide } from "../strategy/components/strategySlides";
import ShareStrategyModal from "@/app/routes/dashboard/strategy/components/ShareStrategyModal";
import { dayMonth, dayMonthYear, relativeDay, statusPillText } from "../strategy/deck/deckModel";
import { DeckStatus, StrategyLoading } from "../strategy/deck/DeckParts";
import StrategyStart, { type StrategyStartRequest } from "../strategy/deck/StrategyStart";
import { GoLiveDialog, ReviewNotesDialog, SimpleConfirm } from "../strategy/deck/StrategyDialogs";
import { KitButton } from "../components/kit/Button";
import { KitIconButton } from "../components/kit/IconButton";
import { KitMenu, KitMenuGap, KitMenuItem, KitMenuPopover, KitMenuTrigger, KitPressable } from "../components/kit/Menu";
import { KitPopover } from "../components/kit/Popover";
import { ErrorState, EmptyState } from "../components/ViewStates";
import "../strategy/deck/deck.css";

const isEmptyDocument = (doc: object | null | undefined) =>
  !Object.entries(doc ?? {}).some(([key, value]) => key !== "schema_version" && value != null);

type Dialog =
  | { kind: "golive" }
  | { kind: "delete" }
  | { kind: "notes" }
  | { kind: "share" }
  | { kind: "stop"; userId: string; name: string };

export default function StrategyView() {
  const { selectedBrandId: brandId } = useWorkspaceStore();
  const { canEdit } = usePermissions();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const brands = useGetBrands();
  const brandName = brands.data?.find((brand) => brand.id === brandId)?.name ?? null;

  // ?strategy=<id> names the open strategy. Without it the page is its index:
  // the start, where the brand's strategies are listed and a new one begins.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = searchParams.get("strategy");

  const list = useStrategies(brandId);
  const { strategies, isLoading, isError: listError } = list;

  /** Opens a strategy, or (null) goes back to the start. A step in history, so Back returns. */
  const setSelectedId = (id: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams();
      if (id) next.set("strategy", id);
      const rail = prev.get("rail");
      if (rail) next.set("rail", rail);
      return next;
    });
  };

  const starting = !activeId;
  const liveDetail = useStrategyDetail(brandId, starting ? null : activeId);
  const { detail, refetch, isError: detailError } = liveDetail;
  const editMedia = useStrategySlideImage(brandId, activeId);
  const current = useMemo(() => strategies.find((s) => s.id === activeId) ?? null, [strategies, activeId]);
  const liveStrategy = strategies.find((s) => s.status === "active") ?? null;
  const empty = !!detail && isEmptyDocument(detail.strategy.document);

  // ?view=; else slide view while nothing is written, the grid once drafted
  // (D57). A link that names a slide opens on it.
  const [arrivedOnSlide] = useState(() => !!searchParams.get("slide"));
  const viewParam = searchParams.get("view");
  const view: DeckView = viewParam === "grid" || viewParam === "slides"
    ? viewParam
    : arrivedOnSlide || empty ? "slides" : "grid";
  const setView = (next: DeckView) =>
    setSearchParams((prev) => { const params = new URLSearchParams(prev); params.set("view", next); return params; }, { replace: true });

  const selectedSlideId = searchParams.get("slide");
  const section = searchParams.get("section");
  const selectSlide = useCallback((slide: StrategySlide) => {
    if (!brandId || !activeId) return;
    useStrategySlideContext.getState().set({ brandId, strategyId: activeId, section: slide.section, label: `${slide.title}${slide.continuation ? ` · continued ${slide.continuation + 1}` : ""} — ${slide.subtitle} (slide key ${slide.key})`, pillarId: slide.pillarId });
    setSearchParams((prev) => {
      if (prev.get("slide") === slide.id && prev.get("section") === slide.section) return prev;
      const next = new URLSearchParams(prev);
      next.set("slide", slide.id); next.set("section", slide.section);
      return next;
    }, { replace: true });
  }, [brandId, activeId, setSearchParams]);
  useEffect(() => () => useStrategySlideContext.getState().set(null), [brandId, activeId, starting]);
  const selectText = useCallback((text: string | null, source?: SlideTextSource) => useStrategySlideContext.getState().setSelection(text, source), []);
  const slideLabels = useCallback((labels: string[]) => useStrategySlideContext.getState().setLabels(labels), []);

  /* --- Creo's focus, and the one live line the rail shows for this page. */
  useAgentFocus(
    starting
      ? { surface: "strategy" }
      : { surface: "strategy", ...(activeId ? { strategyId: activeId } : {}), ...(section ? { section } : {}) },
    { contained: true },
  );
  const [slideCount, setSlideCount] = useState(0);
  const railLine = starting
    ? "Nothing is written yet."
    : !current
        ? null
        : current.status === "active"
          ? "Live."
          : current.status === "archived"
            ? "Archived. Kept for history."
            : empty
              ? "Nothing is written yet."
              : `Drafted. ${slideCount} slides.`;
  useEffect(() => {
    useAgentRailStatusStore.getState().set(railLine);
  }, [railLine]);
  useEffect(() => () => useAgentRailStatusStore.getState().set(null), []);

  /* --- the start: the line typed there goes to Creo as the first message of a
     new chat (D44: sent, not prefilled), with any PDF. Creo creates the
     strategy (CreateStrategy) and the rail opens it here when it does. */
  const agentRunning = useAgentRunStore((s) => s.running);
  const [startSent, setStartSent] = useState(false);
  const [sentFrom, setSentFrom] = useState(activeId);
  if (sentFrom !== activeId) {
    setSentFrom(activeId);
    setStartSent(false);
  }
  const startBusy = startSent && agentRunning;
  const onStart = (request: StrategyStartRequest) => {
    setStartSent(true);
    agentRail.send(request.objective, {
      newSession: true,
      ...(request.modelId ? { modelId: request.modelId } : {}),
      reasoningEffort: request.reasoningEffort,
      ...(request.file ? { files: [request.file] } : {}),
    });
  };

  /* --- rename (inline, in the top bar). */
  const renameStrategy = useRenameStrategy(brandId, activeId);
  const [titleEdit, setTitleEdit] = useState<{ id: string; value: string } | null>(null);
  const commitRename = () => {
    if (!titleEdit) return;
    const trimmed = titleEdit.value.trim();
    setTitleEdit(null);
    if (!trimmed || !current || trimmed === current.title) return;
    renameStrategy.mutate(trimmed, {
      onError: (err) => queue.add({ title: "Could not rename the strategy", description: lifecycleErrorMessage(err), variant: "error" }),
    });
  };

  /* --- lifecycle. */
  const lifecycle = useStrategyLifecycle(brandId, activeId);
  const calendar = useCalendarSettings(canEdit && current?.status !== "active" ? brandId : null);
  const shareable = canEdit && current?.status === "active";
  const shares = useStrategyShares(brandId, activeId, { enabled: shareable });
  const revoke = useRevokeStrategyShare(brandId, activeId);
  // The backend's go-live checks: "Missing: …" lines block going live, the rest are advisory.
  const checks = useMemo(() => detail?.checks ?? [], [detail]);
  const missing = useMemo(() => checks.filter((line) => line.startsWith("Missing: ")).map((line) => line.slice(9).replace(/\.$/, "")), [checks]);
  const canActivate = !!detail && missing.length === 0;
  const reviewNotes = useMemo(
    () => [...checks, ...(detail?.openItems ?? []).map((o) => `Open: ${o.item} · ${o.what_would_settle_it}`)],
    [checks, detail],
  );
  const deleteStrategy = useDeleteStrategy(brandId);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [presenting, setPresenting] = useState(false);

  const goLive = (useStrategyVolume: boolean) => {
    if (!current) return;
    const title = current.title;
    setDialog(null);
    lifecycle.activate.mutate(undefined, {
      onSuccess: async (result) => {
        if (useStrategyVolume && result?.calendarScope?.status === "kept" && brandId) {
          try {
            await axiosInstance.post(`/content-calendar/${brandId}/settings/use-strategy-scope`);
            void qc.invalidateQueries({ queryKey: ["calendarSettings", brandId] });
          } catch {
            queue.add({ title: "The calendar kept its own volume", description: "Use the strategy’s numbers from Calendar settings.", variant: "warning" });
          }
        }
        queue.add({
          title: `“${title}” is live`,
          description: result?.warnings?.length ? `The calendar now plans against its pillars. ${result.warnings.length} check${result.warnings.length === 1 ? " is" : "s are"} still open.` : "The calendar now plans against its pillars.",
          variant: "success",
          action: { label: "Open the calendar", onPress: () => navigate("/dashboard/content-calendar") },
        });
      },
      onError: (err) => queue.add({ title: "Could not go live", description: lifecycleErrorMessage(err), variant: "error" }),
    });
  };
  const runStatus = (action: "archive" | "revertToDraft", done: string) =>
    lifecycle[action].mutate(undefined, {
      onSuccess: () => queue.add({ title: done, variant: "success" }),
      onError: (err) => queue.add({ title: "That did not go through", description: lifecycleErrorMessage(err), variant: "error" }),
    });

  /* --- the page. */
  if (!brandId) {
    return <EmptyState title="No brand selected" body="Pick a brand from the workspace switcher to plan its strategy." />;
  }

  if (listError && strategies.length === 0) {
    return (
      <div className="sd-start">
        <ErrorState title="The strategy did not load" body="Creo could not read this brand’s strategies, so nothing is shown. Nothing you made has changed." onRetry={() => void list.invalidate()} />
      </div>
    );
  }

  if (isLoading) return <StrategyLoading />;

  if (starting) {
    return (
      <StrategyStart
        key={brandId}
        brandName={brandName}
        strategies={strategies}
        canEdit={canEdit}
        busy={startBusy}
        error={null}
        onStart={onStart}
        onOpen={(strategy) => setSelectedId(strategy.id)}
      />
    );
  }

  const version = /^v(\d+)$/.exec(current?.version ?? "")?.[1];
  const shared = shares.data?.shares ?? [];
  const metaLine = (() => {
    if (!current) return null;
    if (current.status === "active") {
      const since = `Live since ${dayMonth(current.liveAt ?? current.updatedAt)}`;
      if (!shared.length) return since;
      const first = shared[0];
      const who = shared.length === 1 ? (first.name || first.email) : `${shared.length} people`;
      const opened = shared.length === 1 && first.lastViewedAt ? ` · opened ${relativeDay(first.lastViewedAt)}` : "";
      return `${since} · with ${who} since ${relativeDay(first.sentAt).replace(/^on /, "")}${opened}`;
    }
    if (current.status === "archived") return `Ran ${dayMonthYear(current.periodStart)} to ${dayMonthYear(current.periodEnd)}`;
    if (empty) return `${dayMonthYear(current.periodStart)} to ${dayMonthYear(current.periodEnd)}`;
    return null;
  })();

  const primary = !canEdit || !current ? null
    : current.status === "active"
      ? <KitButton iconRight={Send} onClick={() => setDialog({ kind: "share" })}>Send to client</KitButton>
      : current.status === "archived"
        ? <KitButton onClick={() => setDialog({ kind: "golive" })} isDisabled={!canActivate}>Make active</KitButton>
        : canActivate
          ? <KitButton onClick={() => setDialog({ kind: "golive" })}>Go live</KitButton>
          : (
            // Not ready: Go live says what is missing, as a list the person can
            // read and act on, not a hover hint they might never see.
            <DialogTrigger>
              <KitPressable><KitButton>Go live</KitButton></KitPressable>
              <KitPopover placement="bottom end" className="sd-golive-pop">
                <Dialog aria-label="Before it can go live" className="outline-none">
                  <p className="sd-golive-pop-head">Before it can go live</p>
                  <p className="sd-golive-pop-text">{missing.length ? "It still needs:" : "Nothing is written yet."}</p>
                  {missing.length ? <ul>{missing.map((line) => <li key={line}>{line}</li>)}</ul> : null}
                </Dialog>
              </KitPopover>
            </DialogTrigger>
          );

  const toolbar = ({ slideCount: count }: { slideCount: number }) => {
    return (
      <div className="sd-top">
        {titleEdit && titleEdit.id === current?.id ? (
          <input
            autoFocus
            className="sd-title-input"
            aria-label="Strategy title"
            value={titleEdit.value}
            onChange={(e) => setTitleEdit({ id: titleEdit.id, value: e.target.value })}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              else if (e.key === "Escape") setTitleEdit(null);
            }}
          />
        ) : (
          <KitMenuTrigger>
            <KitPressable>
              <button type="button" className="sd-title" aria-label={`${current?.title ?? "Strategy"}. All strategies`}>
                {current?.title ?? "Strategy"}
              </button>
            </KitPressable>
            <KitMenuPopover placement="bottom start" className="min-w-[380px]">
              <KitMenu
                aria-label="All strategies"
                onAction={(key) => {
                  if (key === "new") { setSelectedId(null); return; }
                  setSelectedId(String(key));
                }}
              >
                {strategies.map((s) => <StrategyMenuRow key={s.id} strategy={s} current={s.id === activeId} />)}
                {canEdit ? <KitMenuItem id="new" icon={Plus}>New strategy</KitMenuItem> : null}
              </KitMenu>
            </KitMenuPopover>
          </KitMenuTrigger>
        )}
        {current ? <DeckStatus>{statusPillText(current, { empty })}</DeckStatus> : null}
        {metaLine ? <span className="sd-meta">{metaLine}</span> : current?.status === "draft" && !empty ? <span className="sd-meta">{count} slides</span> : null}
        <span className="sd-push" />
        <div className="sd-actions">
          {primary}
          <KitMenuTrigger>
            <KitPressable>
              <KitIconButton kind="glass" size="md" icon={MoreHorizontal} aria-label="More for this strategy" />
            </KitPressable>
            <KitMenuPopover>
              <KitMenu
                aria-label="More for this strategy"
                onAction={(key) => {
                  const action = String(key);
                  if (action === "rename" && current) setTitleEdit({ id: current.id, value: current.title });
                  else if (action === "present") setPresenting(true);
                  else if (action === "notes") setDialog({ kind: "notes" });
                  else if (action === "draft") runStatus("revertToDraft", "Back in the drafts");
                  else if (action === "archive") runStatus("archive", "Archived, and kept for history");
                  else if (action === "delete") setDialog({ kind: "delete" });
                  else if (action === "golive") setDialog({ kind: "golive" });
                  else if (action.startsWith("stop:")) {
                    const share = shared.find((s) => s.userId === action.slice(5));
                    if (share) setDialog({ kind: "stop", userId: share.userId, name: share.name || share.email });
                  }
                }}
              >
                <KitMenuItem id="present">Present</KitMenuItem>
                {reviewNotes.length ? <KitMenuItem id="notes">Before you send · {reviewNotes.length}</KitMenuItem> : null}
                {canEdit && current ? <KitMenuItem id="rename">Rename</KitMenuItem> : null}
                {canEdit && current && current.status !== "draft" ? <KitMenuGap /> : null}
                {canEdit && current?.status === "archived" && canActivate ? <KitMenuItem id="golive">Make active</KitMenuItem> : null}
                {canEdit && current && current.status !== "draft" ? <KitMenuItem id="draft">Back to draft</KitMenuItem> : null}
                {canEdit && current?.status === "active" ? <KitMenuItem id="archive">Archive</KitMenuItem> : null}
                {canEdit && current ? <KitMenuGap /> : null}
                {canEdit && current ? <KitMenuItem id="delete" danger>Delete</KitMenuItem> : null}
                {canEdit && shared.length ? <KitMenuGap /> : null}
                {canEdit ? shared.map((s) => <KitMenuItem key={s.userId} id={`stop:${s.userId}`} danger>Stop sharing with {s.name || s.email}</KitMenuItem>) : null}
              </KitMenu>
            </KitMenuPopover>
          </KitMenuTrigger>
        </div>
      </div>
    );
  };

  return (
    <>
      {listError ? (
        <p role="alert" className="sr-only">Couldn’t refresh the strategy list. Your open work is still available.</p>
      ) : null}
      {detail ? (
        <StrategyDocumentView
          key={activeId}
          view={view}
          onViewChange={setView}
          toolbar={toolbar}
          presenting={presenting}
          onPresentingChange={setPresenting}
          onSlideCount={setSlideCount}
          selectedSlideId={selectedSlideId}
          selectedSection={section}
          onSlideChange={selectSlide}
          onTextSelect={selectText}
          onSlideLabels={slideLabels}
          onMediaChange={canEdit ? editMedia.mutateAsync : undefined}
          brandId={canEdit ? brandId : undefined}
          doc={detail.strategy.document ?? {}}
          meta={{
            brand: detail.brandName || brandName,
            periodStart: current?.periodStart ?? detail.strategy.periodStart,
            periodEnd: current?.periodEnd ?? detail.strategy.periodEnd,
            title: current?.title ?? null,
            record: { status: detail.strategy.status, version: detail.strategy.version, liveAt: detail.strategy.liveAt, updatedAt: detail.strategy.updatedAt },
          }}
          slideImages={detail.slideImages}
          pictureUrls={detail.pictureUrls}
        />
      ) : detailError ? (
        <div className="sd-start">
          <ErrorState title="This strategy did not load" body="Creo could not read it, so nothing is shown. Nothing in it has changed." onRetry={refetch} />
        </div>
      ) : (
        <StrategyLoading />
      )}

      {dialog?.kind === "golive" && current ? (
        <GoLiveDialog
          strategy={current}
          doc={detail?.strategy.document ?? null}
          replaces={liveStrategy}
          calendar={calendar.data ?? null}
          onConfirm={goLive}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "delete" && current ? (
        <SimpleConfirm
          title={`Delete “${current.title}”?`}
          body={current.status === "active" ? "It is the live strategy: the calendar keeps its posts and their pillar names, but plans against no strategy until another goes live. This cannot be undone." : "The strategy and the pictures chosen for its slides go. This cannot be undone."}
          cancelLabel="Keep it"
          confirmLabel="Delete"
          danger
          onClose={() => setDialog(null)}
          onConfirm={() => {
            const id = current.id;
            setDialog(null);
            setSelectedId(null);
            deleteStrategy.mutate(id, {
              onError: (err) => queue.add({ title: "Could not delete the strategy", description: lifecycleErrorMessage(err), variant: "error" }),
            });
          }}
        />
      ) : null}
      {dialog?.kind === "notes" ? <ReviewNotesDialog notes={reviewNotes} onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === "share" && activeId ? (
        <ShareStrategyModal brandId={brandId} strategyId={activeId} strategyTitle={current?.title ?? "this strategy"} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === "stop" ? (
        <SimpleConfirm
          title={`Stop sharing with ${dialog.name}?`}
          body={`${dialog.name} can no longer open ${version ? `version ${version}` : "this strategy"}. You can send it again at any time.`}
          cancelLabel="Keep sharing"
          confirmLabel="Stop sharing"
          danger
          onClose={() => setDialog(null)}
          onConfirm={() => { revoke.mutate(dialog.userId); setDialog(null); }}
        />
      ) : null}
    </>
  );
}

/** A row in All strategies (14 · 00): "<title> · live, version 2", the open one ticked. */
function StrategyMenuRow({ strategy, current }: { strategy: StrategyListItem; current: boolean }) {
  const version = /^v(\d+)$/.exec(strategy.version)?.[1];
  const state = strategy.status === "active" ? "live" : strategy.status === "archived" ? "archived" : "draft";
  const label = `${strategy.title} · ${state}${version ? `, version ${version}` : ""}`;
  return (
    <KitMenuItem id={strategy.id} textValue={label} className={current ? "bg-co-accent-dim text-co-text" : undefined}>
      <span className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {current ? <Check aria-hidden className="size-3 shrink-0 text-co-accent" /> : null}
      </span>
    </KitMenuItem>
  );
}


